/**
 * 合作伙伴门户认证 Store
 * 管理登录状态、token 持久化、2 小时有效窗口
 * 通过后端 /api/auth/login 接口认证
 */
import { create } from 'zustand';
import { authApi, type LoginResponse } from '@/api';
import { getToken, setToken, clearToken } from '@/api/request';

const USER_KEY = 'partner_user';
const LOGIN_TIME_KEY = 'partner_login_time';

/** 登录有效窗口：2 小时 */
const LOGIN_TTL_MS = 2 * 60 * 60 * 1000;

interface AuthUser {
  partnerId?: string;
  username: string;
  displayName: string;
  avatar?: string;
}

function isLoginValid(): boolean {
  if (!getToken()) return false;
  const ts = localStorage.getItem(LOGIN_TIME_KEY);
  if (!ts) return false;
  const last = Number(ts);
  if (Number.isNaN(last)) return false;
  return Date.now() - last < LOGIN_TTL_MS;
}

function restoreUser(): AuthUser | null {
  if (!isLoginValid()) return null;
  try {
    const stored = localStorage.getItem(USER_KEY);
    if (stored) return JSON.parse(stored) as AuthUser;
  } catch {
    // ignore
  }
  return null;
}

interface AuthStore {
  isAuthenticated: boolean;
  currentUser: AuthUser | null;
  mustChangePassword: boolean;
  login: (username: string, password: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  /** 标记 token 已被后端拒绝（401），强制清除登录态 */
  markTokenInvalid: () => void;
  clearMustChangePassword: () => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  isAuthenticated: isLoginValid(),
  currentUser: restoreUser(),
  mustChangePassword: false,

  login: async (username: string, password: string): Promise<LoginResponse> => {
    try {
      const resp: LoginResponse = await authApi.login({ username, password });
      setToken(resp.token);
      const user: AuthUser = {
        partnerId: resp.user.partnerId,
        username: resp.user.username,
        displayName: resp.user.displayName || username,
        avatar: resp.user.avatar,
      };
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      // 记录登录时间戳（用于前端 2 小时窗口判断）
      localStorage.setItem(LOGIN_TIME_KEY, String(Date.now()));
      set({ isAuthenticated: true, currentUser: user, mustChangePassword: resp.mustChangePassword ?? false });
      // 强制刷新合作伙伴信息，确保顶栏公司名等信息实时更新
      try {
        const { usePartnerStore } = await import('./partnerStore');
        // 先重置所有缓存（包括 globalLoaded），确保 preloadGlobal 重新拉数据
        usePartnerStore.setState({
          partnerInfo: null,
          dashboardStats: null,
          announcements: [],
          activities: [],
          certifications: [],
          globalLoaded: false,
        });
        await usePartnerStore.getState().fetchPartnerInfo(true);
      } catch (e) {
        // 静默失败，但不掩盖（方便排查）
        console.warn('[authStore] 刷新合作伙伴信息失败:', e);
      }
      return resp;
    } catch (err: unknown) {
      // 将错误消息抛出供 LoginPage 显示
      const msg = err instanceof Error ? err.message : '登录失败';
      throw new Error(msg);
    }
  },

  logout: async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // 后端登出失败不影响前端登出流程
    }
    // 清空合作伙伴信息缓存，确保下次登录拿到的是新账号的数据
    try {
      const { usePartnerStore } = await import('./partnerStore');
      usePartnerStore.setState({
        partnerInfo: null,
        dashboardStats: null,
        announcements: [],
        activities: [],
        certifications: [],
        globalLoaded: false,
      });
    } catch {}
    clearToken();
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LOGIN_TIME_KEY);
    set({ isAuthenticated: false, currentUser: null, mustChangePassword: false });
  },

  /** 后端 401 时调用 — 真正失效时清掉登录态，下次进入跳登录页 */
  markTokenInvalid: (): void => {
    clearToken();
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LOGIN_TIME_KEY);
    set({ isAuthenticated: false, currentUser: null, mustChangePassword: false });
  },

  clearMustChangePassword: (): void => {
    set({ mustChangePassword: false });
  },
}));

export { LOGIN_TTL_MS };
